// @ts-nocheck

import {useTranslation} from "@/Utils/CustomHooks/useTranslation";

export const checkPasswordStrength = (password = "") => {
    const checks = {
        minLength: password.length >= 12,
        hasNumber: /\d/.test(password),
        hasUppercase: /[A-Z]/.test(password),
        hasSpecialChar: /[^A-Za-z0-9]/.test(password),
    };

    const score = Object.values(checks).filter(Boolean).length;

    return {
        ...checks,
        score,
        isValid: score >= 3,
    };
};


/*
 * Textbausteine werden als HTML gespeichert (CKEditor 5, editor.getData()).
 * Fuer Listen-Vorschau und Popover brauchen wir daraus Klartext.
 *
 * ACHTUNG -- in den Echtdaten kommen ZWEI Formen vor:
 *   a) echtes HTML:      "<p>Text</p><br>"
 *   b) entity-escaped:   "&lt;p&gt;Text&lt;/p&gt;&lt;br&gt;"
 *
 * Deshalb genuegt kein einzelner Durchlauf: entfernt man erst die Tags und
 * dekodiert danach die Entities, tauchen bei Form (b) die Tags als Klartext
 * wieder auf ("...2009<br><br><b>1. Definition</b>"). Umgekehrt kann man
 * nicht einfach vorher dekodieren, weil dann in Form (a) enthaltene
 * escapte Zeichen faelschlich als Markup gelesen wuerden.
 *
 * Loesung: Durchlauf wiederholen, bis sich der Text nicht mehr aendert.
 * Prosa mit einzelnem "<" (z.B. "Preis < 100") bleibt unangetastet, weil
 * ein solcher Durchlauf nichts veraendert und die Schleife dann abbricht.
 *
 * Das Ergebnis wird ausschliesslich als React-Text gerendert, NIE per
 * dangerouslySetInnerHTML -- deshalb ist kein Sanitizer notwendig.
 */
const HTML_MAX_PASSES = 4;

const decodeAndStripOnce = (input: string): string => {
    // Blockgrenzen und <br> zu echten Zeilenumbruechen machen, damit
    // Absaetze nicht zu einem Wortsalat zusammenlaufen.
    const withBreaks = input
        .replace(/<\s*br\s*\/?\s*>/gi, "\n")
        .replace(/<\s*\/\s*(p|div|li|tr|h[1-6])\s*>/gi, "\n");

    if (typeof DOMParser !== "undefined") {
        // textContent entfernt Tags UND dekodiert HTML-Entities in einem Schritt.
        return (
            new DOMParser().parseFromString(withBreaks, "text/html").body
                ?.textContent ?? ""
        );
    }

    // Fallback ohne DOM (z.B. Node ohne jsdom).
    // Nur echte Tags entfernen: "<" muss von einem Buchstaben oder "/"
    // gefolgt sein. Sonst frisst der Ausdruck Prosa wie "Preis < 100 und > 3".
    return withBreaks
        .replace(/<\/?[a-zA-Z][^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/g, "'")
        .replace(/&#(\d+);/g, (_m, d) => String.fromCharCode(Number(d)))
        .replace(/&#x([0-9a-f]+);/gi, (_m, h) => String.fromCharCode(parseInt(h, 16)));
};

export const htmlToPlainText = (html?: string | null): string => {
    if (!html) {
        return "";
    }

    let text = String(html);

    for (let pass = 0; pass < HTML_MAX_PASSES; pass++) {
        const next = decodeAndStripOnce(text);

        // Stabil -> fertig. Auch der Schutz fuer Prosa mit "<" oder "&".
        if (next === text) {
            break;
        }

        text = next;

        // Nichts Markup-artiges mehr uebrig -> kein weiterer Durchlauf noetig.
        if (!/[<&]/.test(text)) {
            break;
        }
    }

    return text
        .replace(/\u00a0/g, " ")
        .split("\n")
        .map((line) => line.replace(/[ \t]+/g, " ").trim())
        .join("\n")
        .replace(/\n{2,}/g, "\n")
        .trim();
};

/*
 * Einzeilige Vorschau: Zeilenumbrueche werden als Pilcrow sichtbar gemacht,
 * damit erkennbar bleibt, dass der Text mehrzeilig ist.
 */
export const PARAGRAPH_MARKER = " \u00b6 ";

export const htmlToOneLine = (html?: string | null): string =>
    htmlToPlainText(html).replace(/\n+/g, PARAGRAPH_MARKER);


export default class Common {
    static areDatesDefined(startdate, enddate): boolean {
        return (startdate && enddate) || (!startdate && !enddate)
            ? true
            : false;
    }

    /*
    * Parses a time string (0830 -> 08:30 OR 08:30 -> 0830 if inverse == true)
    * */
    static formatTimeString(str: string, inverse: boolean = false): string {
        if (!str || str.length < 3) {
            return inverse ? "0000" : "00:00";
        }

        if (str.length == 3) {
            str = '0' + str;
        }

        if (inverse) {
            return str.replace(':', '');
        }

        return str.substring(0, 2) + ':' + str.substring(2);
    }

    /*
    * Returns the last day of a given Date objects month as Date object
    * */
    static getLastDayOfMonth(date: Date): Date {
        const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        const year = lastDayOfMonth.getFullYear();
        const month = String(lastDayOfMonth.getMonth() + 1).padStart(2, '0');
        const day = String(lastDayOfMonth.getDate()).padStart(2, '0');

        return `${year}-${month}-${day} 00:00:00.000`;
    }

    /*
   * Finds and parses a fieldAction object
   * */
    static findAndParseFieldActions(fieldActions, action: string): object {
        let fieldAction = {};

        fieldActions?.forEach((jsonField) => {
            let field = JSON.parse(jsonField.JSONResponse);
            field = field[Object.keys(field)[0]]?.fields[0];

            if (field.name == action) {
                fieldAction = field;
                return;
            }
        })
        if (Object.keys(fieldAction).length == 0){

            fieldAction["name"] = action
            fieldAction["visible"] = true
            fieldAction["enabled"] = true
        }
        return fieldAction
    }

}
